import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Page,
  Card,
  ResourceList,
  ResourceItem,
  TextField,
  Button,
  ButtonGroup,
  Text,
  Badge,
  EmptyState,
  Popover,
  ActionList,
  Banner
} from '@shopify/polaris';
import { useShop } from '../contexts/ShopContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ShopifyBulkOperationsService from '../services/shopifyBulkOperationsService';
import { getUserSubscription, getSubscriptionPlans } from '../services/subscriptionService';
import { getUserCreditStatus } from '../services/creditService';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

export default function Products() {
  const { currentShop } = useShop();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [typePopoverActive, setTypePopoverActive] = useState(false);
  const [tagPopoverActive, setTagPopoverActive] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [productCache, setProductCache] = useState({});
  const [currentStatus, setCurrentStatus] = useState('DRAFT');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [maxProductSelection, setMaxProductSelection] = useState(5); // Default to 5
  const [selectionLimitExceeded, setSelectionLimitExceeded] = useState(false);
  const navigate = useNavigate();

  // Function to fetch products with status
  const fetchProducts = async (status = 'DRAFT') => {
    if (!currentShop) {
      console.log('No shop selected');
      return;
    }

    // Check cache first
    if (productCache[status]) {
      console.log(`Using cached ${status} products:`, productCache[status].length);
      setProducts(productCache[status]);
      return;
    }

    try {
      setLoading(true);
      
      const bulkService = new ShopifyBulkOperationsService(
        currentShop.shop,
        currentShop.accessToken
      );

      console.log('Fetching products with status:', status);
      
      let bulkOperation;
      try {
        const query = buildBulkQuery(status);
        bulkOperation = await bulkService.startBulkProductsQuery(query);
      } catch (error) {
        // If there's already an operation running, just wait for it
        if (error.message && error.message.includes('already in progress')) {
          console.log('Existing operation detected, waiting for completion...');
          const currentOperation = await bulkService.checkBulkOperationStatus();
          if (currentOperation && currentOperation.status === 'RUNNING') {
            await bulkService.pollOperationStatus();
            // Try the operation again after waiting
            const query = buildBulkQuery(status);
            bulkOperation = await bulkService.startBulkProductsQuery(query);
          }
        } else {
          throw error; // Re-throw if it's a different error
        }
      }
      
      if (!bulkOperation?.id) {
        throw new Error('Failed to start bulk operation - no operation ID received');
      }
      
      const jsonlData = await bulkService.pollBulkOperation(bulkOperation.id);
      
      if (!jsonlData || typeof jsonlData !== 'string' || jsonlData.trim() === '') {
        const emptyProducts = [];
        setProductCache(prev => ({
          ...prev,
          [status]: emptyProducts
        }));
        setProducts(emptyProducts);
        setError(''); // Clear any previous errors
        return;
      }
      
      // Process the JSONL data to get an array of products
      const results = bulkService.processProductsData(jsonlData);
      
      if (!results || !Array.isArray(results) || results.length === 0) {
        const emptyProducts = [];
        setProductCache(prev => ({
          ...prev,
          [status]: emptyProducts
        }));
        setProducts(emptyProducts);
        setError(''); // Clear any previous errors
        return;
      }

      // Process and filter products
      const processedProducts = results
        .filter(product => product.id && product.id.startsWith('gid://shopify/Product/'))
        .filter(product => product.status === status)
        .map(product => ({
          id: product.id,
          title: product.title,
          status: product.status,
          tags: Array.isArray(product.tags) ? product.tags : product.tags?.split(',') || [],
          productType: product.productType || '',
          featuredImage: product.featuredImage ? {
            id: product.featuredImage.id,
            url: product.featuredImage.url,
            altText: product.featuredImage.altText
          } : null
        }));

      // Update cache with the new products
      setProductCache(prev => ({
        ...prev,
        [status]: processedProducts
      }));

      setProducts(processedProducts);
      console.log('Processed products:', processedProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
      setError(`Failed to load products: ${error.message}`);
      setProductCache(prev => ({
        ...prev,
        [status]: []
      }));
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Clear cache when shop changes
  useEffect(() => {
    if (currentShop) {
      setProductCache({});
      setCurrentStatus('DRAFT');
      fetchProducts('DRAFT');
    }
  }, [currentShop]);

  // Initial load of draft products
  useEffect(() => {
    const initializeProducts = async () => {
      if (!currentShop) return;

      try {
        setCurrentStatus('DRAFT');
        await fetchProducts('DRAFT');
      } catch (error) {
        console.error('Error initializing products:', error);
        setError('Failed to load products. Please try again in a few moments.');
      }
    };

    initializeProducts();
  }, [currentShop]);

  // Add debug logging for user object when component mounts
  useEffect(() => {
    console.log('Products component mounted, user object:', user);
    // Automatically fetch subscription limits when component mounts
    const initializeSubscriptionLimits = async () => {
      // Wait a short time to ensure Firebase auth is initialized
      setTimeout(async () => {
        try {
          const currentUser = auth.currentUser;
          if (currentUser && currentUser.uid) {
            console.log('Auto-fetching subscription limits on page load');
            await fetchSubscriptionDetails();
          } else {
            console.log('No authenticated user available yet, will try again');
            // Try again in 1 second if user isn't available yet
            setTimeout(() => {
              const retryUser = auth.currentUser;
              if (retryUser && retryUser.uid) {
                console.log('Retry: Auto-fetching subscription limits');
                fetchSubscriptionDetails();
              } else {
                console.log('Still no authenticated user available after retry');
              }
            }, 1000);
          }
        } catch (error) {
          console.error('Error initializing subscription limits:', error);
        }
      }, 500);
    };
    
    initializeSubscriptionLimits();
  }, []);

  // Handle status filter change
  const handleStatusChange = async (status) => {
    // Always update the current status immediately
    setCurrentStatus(status);
    // Clear any previous errors when switching tabs
    setError('');
    
    if (status === 'ALL') {
      // For ALL tab, combine existing caches or fetch what's missing
      const draftProducts = productCache['DRAFT'] || [];
      const activeProducts = productCache['ACTIVE'] || [];
      const archivedProducts = productCache['ARCHIVED'] || [];
      
      // If we don't have ACTIVE products yet, fetch them
      if (activeProducts.length === 0 && !loading) {
        await fetchProducts('ACTIVE');
        // Don't return here, continue to check other statuses
      }
      
      // If we don't have DRAFT products yet, fetch them
      if (draftProducts.length === 0 && !loading) {
        await fetchProducts('DRAFT');
        // Don't return here, continue to check other statuses
      }
      
      // If we don't have ARCHIVED products yet, fetch them
      if (archivedProducts.length === 0 && !loading) {
        await fetchProducts('ARCHIVED');
        // Don't return here, continue to combine all products
      }
      
      // Get the latest products from cache after potential fetches
      const updatedDraftProducts = productCache['DRAFT'] || [];
      const updatedActiveProducts = productCache['ACTIVE'] || [];
      const updatedArchivedProducts = productCache['ARCHIVED'] || [];
      
      // We have all, combine them
      console.log('Using products for ALL tab');
      setProducts([...updatedDraftProducts, ...updatedActiveProducts, ...updatedArchivedProducts]);
    } else {
      // For specific status tabs, always fetch fresh data
      await fetchProducts(status);
    }
  };

  // Process and sort products before display
  const sortedProducts = useMemo(() => {
    return [...products].sort((a, b) => {
      // First sort by status priority
      const statusPriority = {
        'ACTIVE': 1,
        'DRAFT': 2,
        'ARCHIVED': 3
      };
      
      const statusDiff = (statusPriority[a.status] || 99) - (statusPriority[b.status] || 99);
      if (statusDiff !== 0) return statusDiff;
      
      // Then sort by title
      return (a.title || '').localeCompare(b.title || '');
    });
  }, [products]);

  // Extract unique product types and tags
  const { productTypes, allTags } = useMemo(() => {
    const types = new Set();
    const tags = new Set();
    
    sortedProducts.forEach(product => {
      if (product.productType) {
        types.add(product.productType);
      }
      
      const productTags = Array.isArray(product.tags) 
        ? product.tags 
        : (product.tags || '').split(',').map(t => t.trim());
      
      productTags.forEach(tag => tag && tags.add(tag));
    });
    
    return {
      productTypes: Array.from(types).sort(),
      allTags: Array.from(tags).sort()
    };
  }, [sortedProducts]);

  // Filter handlers
  const handleTypeSelection = (type) => {
    if (selectedTypes.includes(type)) {
      setSelectedTypes(selectedTypes.filter(t => t !== type));
    } else {
      setSelectedTypes([...selectedTypes, type]);
    }
  };

  const handleTagSelection = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleSelectAllTypes = () => {
    setSelectedTypes(productTypes);
    setTypePopoverActive(false);
  };

  const handleDeselectAllTypes = () => {
    setSelectedTypes([]);
    setTypePopoverActive(false);
  };

  const handleSelectAllTags = () => {
    setSelectedTags(allTags);
    setTagPopoverActive(false);
  };

  const handleDeselectAllTags = () => {
    setSelectedTags([]);
    setTagPopoverActive(false);
  };

  const handleRefresh = () => {
    fetchProducts(currentStatus);
  };

  // Extract numeric ID from Shopify ID
  const getNumericId = (shopifyId) => {
    return shopifyId.split('/').pop();
  };

  const filteredProducts = useMemo(() => {
    return (sortedProducts || []).filter(product => {
      if (!product || !product.title) return false;
      
      // Title search
      const matchesSearch = product.title.toLowerCase().includes((searchValue || '').toLowerCase());
      
      // Product type filter
      const matchesType = selectedTypes.length === 0 || 
        selectedTypes.includes(product.productType || '');
      
      // Tag filtering - normalize tags to array format
      const productTags = (Array.isArray(product.tags) ? product.tags.join(',') : (product.tags || ''))
        .split(',')
        .map(tag => tag.trim())
        .filter(Boolean);
      
      const matchesTags = selectedTags.length === 0 || 
        selectedTags.some(tag => productTags.includes(tag));

      return matchesSearch && matchesType && matchesTags;
    });
  }, [sortedProducts, searchValue, selectedTypes, selectedTags]);

  const bulkActions = [
    {
      content: `Edit ${selectedItems.length} Selected Products`,
      onAction: () => {
        const numericIds = selectedItems.map(getNumericId);
        navigate(`/app/product/bulk-edit?ids=${numericIds.join(',')}`);
      },
    },
    {
      content: `Edit ${selectedItems.length} Product Images`,
      onAction: () => {
        const numericIds = selectedItems.map(getNumericId);
        navigate(`/app/product/bulk-edit-images?ids=${numericIds.join(',')}`);
      },
    }
  ];

  // Selection mode config
  const resourceName = {
    singular: 'product',
    plural: 'products',
  };

  const renderItem = (item) => {
    if (!item) return null;
    
    // Always split tags since they're used for sorting and search
    const tags = (typeof item.tags === 'string' ? item.tags : item.tags.join(',')).split(',')
      .map(tag => tag.trim())
      .filter(Boolean);
    
    return (
      <ResourceItem
        id={item.id}
        media={
          <div style={{ width: '50px', height: '50px', overflow: 'hidden', borderRadius: '4px' }}>
            <img
              src={item.featuredImage?.url || '/images/no-image.png'}
              alt={item.featuredImage?.altText || item.title}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/images/no-image.png';
              }}
            />
          </div>
        }
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Text variant="bodyMd" fontWeight="bold">
            {item.title}
          </Text>
          
          {item.productType && (
            <Button
              size="slim"
              monochrome
              outline
              onClick={(e) => {
                e.stopPropagation();
                setSelectedTypes([item.productType]);
              }}
              icon={
                <svg viewBox="0 0 20 20" style={{ width: '16px', height: '16px', marginRight: '4px' }}>
                  <path
                    d="M17.707 9.293l-5-5a.999.999 0 00-1.414 0l-10 10a.999.999 0 000 1.414l5 5a.997.997 0 001.414 0l10-10a.999.999 0 000-1.414zM7 15l-5-5 9-9 5 5-9 9z"
                    fill="currentColor"
                  />
                </svg>
              }
            >
              {item.productType}
            </Button>
          )}
          
          {tags.map((tag, index) => (
            <Button
              key={index}
              size="slim"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedTags([tag]);
              }}
              icon={
                <svg viewBox="0 0 20 20" style={{ width: '16px', height: '16px', marginRight: '4px' }}>
                  <path
                    d="M6 10a4 4 0 118 0 4 4 0 01-8 0zm4-6a6 6 0 100 12 6 6 0 000-12z"
                    fill="currentColor"
                  />
                </svg>
              }
            >
              {tag}
            </Button>
          ))}
          
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Badge status={item.status === 'ACTIVE' ? 'success' : item.status === 'ARCHIVED' ? 'warning' : 'attention'}>
              {item.status}
            </Badge>
            <ButtonGroup>
              <Button onClick={(e) => {
                e.stopPropagation();
                const numericId = getNumericId(item.id);
                navigate(`/app/product/bulk-edit-images?ids=${numericId}`);
              }}>
                Edit Images
              </Button>
              <Button onClick={(e) => {
                e.stopPropagation();
                const numericId = getNumericId(item.id);
                navigate(`/app/product/bulk-edit?ids=${numericId}`);
              }}>
                Edit
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </ResourceItem>
    );
  };

  const handleSelectionChange = useCallback((selectedItems) => {
    if (selectedItems.length > maxProductSelection) {
      setSelectionLimitExceeded(true);
    } else {
      setSelectionLimitExceeded(false);
    }
    setSelectedItems(selectedItems);
  }, [maxProductSelection]);

  const buildBulkQuery = (status) => {
    return `
      {
        products(first: 250, query: "status:${status}") {
          edges {
            node {
              id
              title
              status
              tags
              productType
              featuredImage {
                id
                url
                altText
              }
            }
          }
        }
      }
    `;
  };

  // Fetch subscription details to get max product selection limit
  const fetchSubscriptionDetails = async () => {
    try {
      if (!user) return;
      
      // Get user credit status which includes both subscription and VIP status
      const creditStatus = await getUserCreditStatus(user.uid);
      const { subscription, isVIP, vipPlan } = creditStatus;
      
      console.log('User credit status:', creditStatus);
      
      // First check if maxProductSelection is directly set on the user document (for VIP users)
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();
      
      if (userData && typeof userData.maxProductSelection === 'number') {
        console.log(`Using maxProductSelection ${userData.maxProductSelection} from user document`);
        setMaxProductSelection(userData.maxProductSelection);
        return;
      }
      
      // Check for VIP subscription
      if (isVIP && vipPlan) {
        console.log('User has VIP access with plan:', vipPlan);
        
        // Get the plan details directly from Firestore
        const planDoc = await getDoc(doc(db, 'subscriptionPlans', vipPlan));
        
        if (planDoc.exists()) {
          const planData = planDoc.data();
          console.log(`VIP Plan ${vipPlan} details:`, planData);
          
          if (planData && typeof planData.maxProductSelection === 'number') {
            console.log(`Setting max product selection to ${planData.maxProductSelection} from VIP plan`);
            setMaxProductSelection(planData.maxProductSelection);
            return;
          }
        }
      }
      
      // If no VIP plan or maxProductSelection not found, check regular subscription
      if (!subscription) {
        console.log('No subscription found');
        return;
      }
      
      // Get the plan type from the user's subscription
      const planType = subscription.type;
      console.log('User plan type:', planType);
      
      if (!planType) {
        console.log('No plan type found in subscription');
        return;
      }
      
      // Get the plan details directly from Firestore
      const planDoc = await getDoc(doc(db, 'subscriptionPlans', planType));
      
      if (!planDoc.exists()) {
        console.log(`Plan document ${planType} not found in Firestore`);
        return;
      }
      
      const planData = planDoc.data();
      console.log(`Plan ${planType} details:`, planData);
      
      // Check if the plan has a maxProductSelection property
      if (planData && typeof planData.maxProductSelection === 'number') {
        console.log(`Setting max product selection to ${planData.maxProductSelection} from Firestore plan document`);
        setMaxProductSelection(planData.maxProductSelection);
      } else {
        console.log('No valid maxProductSelection found in plan document');
        // Fallback to default values based on plan type
        if (planType === 'plan_2') {
          console.log('Using fallback value of 15 for plan_2');
          setMaxProductSelection(15);
        } else if (planType === 'plan_3') {
          console.log('Using fallback value of 50 for plan_3');
          setMaxProductSelection(50);
        } else if (planType === 'plan_4') {
          console.log('Using fallback value of 100 for plan_4');
          setMaxProductSelection(100);
        } else if (planType === 'plan_5') {
          console.log('Using fallback value of 250 for plan_5');
          setMaxProductSelection(250);
        } else {
          console.log('Using default fallback value of 5');
          setMaxProductSelection(5);
        }
      }
    } catch (error) {
      console.error('Error fetching subscription details:', error);
    }
  };

  // Fetch subscription limit when component mounts or when currentShop changes
  useEffect(() => {
    console.log('Subscription check useEffect triggered', { 
      userExists: !!user, 
      userHasId: user?.id ? true : false,
      currentShopExists: !!currentShop 
    });
    
    if (user && user.id && currentShop) {
      console.log('User and shop available, fetching subscription limit');
      // Force refresh on initial load
      fetchSubscriptionDetails();
    } else {
      console.log('Missing required data:', {
        user: user ? 'exists' : 'missing',
        userId: user?.id ? 'exists' : 'missing',
        currentShop: currentShop ? 'exists' : 'missing'
      });
    }
  }, [user, currentShop]);

  return (
    <Page
      title="Products"
      subtitle={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {currentShop && (
            <Badge status="success">
              {currentShop.name || currentShop.shop}
            </Badge>
          )}
          <Text variant="bodyMd">
            {loading 
              ? "Loading..." 
              : `${filteredProducts.length} products found${currentStatus !== 'ALL' ? ` (${currentStatus.toLowerCase()} only)` : ''}`}
          </Text>
        </div>
      }
      fullWidth
    >
      {!currentShop ? (
        <EmptyState
          heading="No shop selected"
          action={{
            content: 'Select a shop',
            onAction: () => navigate('/app/shops')
          }}
          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
        >
          <p>Please select a shop to view and manage products.</p>
        </EmptyState>
      ) : (
        <>
          <Card>
            <Card.Section>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <Text variant="headingMd">Product Selection Limit: {selectedItems.length}/{maxProductSelection}</Text>
                  <Text variant="bodySm">Based on your current subscription plan</Text>
                  {selectedItems.length > 0 && (
                    <Text variant="bodySm" color={selectedItems.length > maxProductSelection ? 'critical' : ''}>
                      {selectedItems.length > maxProductSelection 
                        ? `You've exceeded your limit by ${selectedItems.length - maxProductSelection} products` 
                        : `You can select ${maxProductSelection - selectedItems.length} more products`}
                    </Text>
                  )}
                </div>
              </div>
            </Card.Section>
          </Card>
          <div style={{ padding: '16px 0' }}>
            <ButtonGroup segmented>
              <Button
                pressed={currentStatus === 'ALL'}
                onClick={() => handleStatusChange('ALL')}
              >
                All
              </Button>
              <Button
                pressed={currentStatus === 'DRAFT'}
                onClick={() => handleStatusChange('DRAFT')}
              >
                Draft
              </Button>
              <Button
                pressed={currentStatus === 'ACTIVE'}
                onClick={() => handleStatusChange('ACTIVE')}
              >
                Active
              </Button>
              <Button
                pressed={currentStatus === 'ARCHIVED'}
                onClick={() => handleStatusChange('ARCHIVED')}
              >
                Archived
              </Button>
            </ButtonGroup>
          </div>
          <div style={{ padding: '16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <TextField
                label="Search products"
                value={searchValue}
                onChange={setSearchValue}
                clearButton
                onClearButtonClick={() => setSearchValue('')}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Type Filter Popover */}
              <Popover
                active={typePopoverActive}
                activator={
                  <Button 
                    onClick={() => setTypePopoverActive(!typePopoverActive)}
                    disclosure
                    icon={
                      <svg viewBox="0 0 20 20" style={{ width: '16px', height: '16px' }}>
                        <path
                          d="M17.707 9.293l-5-5a.999.999 0 00-1.414 0l-10 10a.999.999 0 000 1.414l5 5a.997.997 0 001.414 0l10-10a.999.999 0 000-1.414zM7 15l-5-5 9-9 5 5-9 9z"
                          fill="currentColor"
                        />
                      </svg>
                    }
                  >
                    Types ({selectedTypes.length})
                  </Button>
                }
                onClose={() => setTypePopoverActive(false)}
              >
                <ActionList
                  actionRole="menuitem"
                  items={[
                    { content: 'Select All', onAction: handleSelectAllTypes },
                    { content: 'Deselect All', onAction: handleDeselectAllTypes },
                    { content: 'Divider', role: 'presentation' },
                    ...productTypes.map(type => ({
                      content: type,
                      active: selectedTypes.includes(type),
                      onAction: () => handleTypeSelection(type)
                    }))
                  ]}
                />
              </Popover>

              {/* Tag Filter Popover */}
              <Popover
                active={tagPopoverActive}
                activator={
                  <Button 
                    onClick={() => setTagPopoverActive(!tagPopoverActive)}
                    disclosure
                    icon={
                      <svg viewBox="0 0 20 20" style={{ width: '16px', height: '16px' }}>
                        <path
                          d="M6 10a4 4 0 118 0 4 4 0 01-8 0zm4-6a6 6 0 100 12 6 6 0 000-12z"
                          fill="currentColor"
                        />
                      </svg>
                    }
                  >
                    Tags ({selectedTags.length})
                  </Button>
                }
                onClose={() => setTagPopoverActive(false)}
              >
                <ActionList
                  actionRole="menuitem"
                  items={[
                    { content: 'Select All', onAction: handleSelectAllTags },
                    { content: 'Deselect All', onAction: handleDeselectAllTags },
                    { content: 'Divider', role: 'presentation' },
                    ...allTags.map(tag => ({
                      content: tag,
                      active: selectedTags.includes(tag),
                      onAction: () => handleTagSelection(tag)
                    }))
                  ]}
                />
              </Popover>

              {/* Active filters display */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                {selectedTypes.map(type => (
                  <Button
                    key={type}
                    size="slim"
                    monochrome
                    outline
                    onClick={() => setSelectedTypes(selectedTypes.filter(t => t !== type))}
                    icon={
                      <svg viewBox="0 0 20 20" style={{ width: '16px', height: '16px', marginRight: '4px' }}>
                        <path
                          d="M17.707 9.293l-5-5a.999.999 0 00-1.414 0l-10 10a.999.999 0 000 1.414l5 5a.997.997 0 001.414 0l10-10a.999.999 0 000-1.414zM7 15l-5-5 9-9 5 5-9 9z"
                          fill="currentColor"
                        />
                      </svg>
                    }
                  >
                    {type} ×
                  </Button>
                ))}
                {selectedTags.map(tag => (
                  <Button
                    key={tag}
                    size="slim"
                    onClick={() => setSelectedTags(selectedTags.filter(t => t !== tag))}
                    icon={
                      <svg viewBox="0 0 20 20" style={{ width: '16px', height: '16px', marginRight: '4px' }}>
                        <path
                          d="M6 10a4 4 0 118 0 4 4 0 01-8 0zm4-6a6 6 0 100 12 6 6 0 000-12z"
                          fill="currentColor"
                        />
                      </svg>
                    }
                  >
                    {tag} ×
                  </Button>
                ))}
                {(selectedTypes.length > 0 || selectedTags.length > 0) && (
                  <Button
                    plain
                    onClick={() => {
                      setSelectedTypes([]);
                      setSelectedTags([]);
                    }}
                  >
                    Clear all filters
                  </Button>
                )}
              </div>
            </div>

            <div style={{ marginTop: '16px' }}>
              {selectionLimitExceeded && (
                <Banner status="warning" onDismiss={() => setSelectionLimitExceeded(false)} style={{ marginBottom: '16px' }}>
                  <p>You have exceeded the maximum product selection limit of <strong>{maxProductSelection}</strong> for your subscription plan. Please deselect some products or <Button plain onClick={() => navigate('/app/settings/subscription')}>upgrade your plan</Button> to continue.</p>
                </Banner>
              )}
              <ResourceList
                resourceName={resourceName}
                items={filteredProducts}
                renderItem={renderItem}
                loading={loading}
                selectable
                selectedItems={selectedItems}
                onSelectionChange={handleSelectionChange}
                promotedBulkActions={selectedItems.length > maxProductSelection ? [] : bulkActions}
                bulkActions={selectedItems.length > maxProductSelection ? [] : bulkActions}
                emptyState={
                  <EmptyState
                    heading={`No ${currentStatus.toLowerCase() === 'all' ? '' : currentStatus.toLowerCase()} products found`}
                    image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  >
                    <p>
                      {searchValue || selectedTypes.length > 0 || selectedTags.length > 0
                        ? "No products match your search criteria."
                        : currentStatus === 'ALL'
                          ? "This shop doesn't have any products yet."
                          : `This shop doesn't have any ${currentStatus.toLowerCase()} products yet.`}
                    </p>
                  </EmptyState>
                }
              />
            </div>
          </div>
        </>
      )}
    </Page>
  );
}
